// PKGV.A3 (#794) — pure, framework-free filter + label-ranking logic for
// PKGNeighbourhoodPanel's client-side filters (S1: never re-fetches;
// operates entirely on an already-fetched PKGNode[]/PKGEdge[] pair).
//
// V4 (locked): an edge is visible iff its own edgeType is checked AND
// both endpoints are in the visible (type-filtered) node set — AND of
// both conditions, never either alone. No dangling edges to hidden nodes.

import type { PKGNode, PKGEdge, PKGNodeEntityType, PKGEdgeType } from '@blueprint/shared';

export function nodeLabel(node: PKGNode): string {
  return node.summary ?? node.entityId;
}

// Display names for the filter checkboxes. No existing display-name map
// exists in the repo for these enums (each node component hardcodes its
// own label) — this one is local to A3's filter UI, not a new shared
// contract. Covers all 10 real entity types (not the issue's 6) and all
// 6 real edge types, per L4.
const ENTITY_TYPE_DISPLAY: Record<PKGNodeEntityType, string> = {
  decision: 'Decision',
  risk: 'Risk',
  requirement: 'Requirement',
  user_story: 'Story',
  meeting: 'Meeting',
  artefact: 'Artefact',
  issue: 'Issue',
  adr_label: 'ADR Label',
  risk_label: 'Risk Label',
  issue_label: 'Issue Label',
};

const EDGE_TYPE_DISPLAY: Record<PKGEdgeType, string> = {
  EVIDENCE_LINK: 'Evidence Link',
  DECISION_OF: 'Decision Of',
  RISK_OF: 'Risk Of',
  IMPLEMENTS: 'Implements',
  DERIVED_FROM: 'Derived From',
  REFERENCES: 'References',
};

export function entityTypeDisplayName(type: PKGNodeEntityType): string {
  return ENTITY_TYPE_DISPLAY[type] ?? type;
}

export function edgeTypeDisplayName(type: PKGEdgeType): string {
  return EDGE_TYPE_DISPLAY[type] ?? type;
}

export function presentNodeTypes(nodes: PKGNode[]): PKGNodeEntityType[] {
  return Array.from(new Set(nodes.map(n => n.entityType)));
}

export function presentEdgeTypes(edges: PKGEdge[]): PKGEdgeType[] {
  return Array.from(new Set(edges.map(e => e.edgeType)));
}

export function typeCounts(nodes: PKGNode[]): Map<PKGNodeEntityType, number> {
  const counts = new Map<PKGNodeEntityType, number>();
  for (const n of nodes) counts.set(n.entityType, (counts.get(n.entityType) ?? 0) + 1);
  return counts;
}

export function edgeTypeCounts(edges: PKGEdge[]): Map<PKGEdgeType, number> {
  const counts = new Map<PKGEdgeType, number>();
  for (const e of edges) counts.set(e.edgeType, (counts.get(e.edgeType) ?? 0) + 1);
  return counts;
}

export interface FilteredSubgraph {
  nodes: PKGNode[];
  edges: PKGEdge[];
}

/**
 * Client-side filter (S1). Pure function over already-fetched data — no
 * fetch, no side effect. visibleTypes/visibleEdgeTypes are the CHECKED
 * sets (not the hidden ones).
 */
export function filterSubgraph(
  nodes: PKGNode[],
  edges: PKGEdge[],
  visibleTypes: Set<PKGNodeEntityType>,
  visibleEdgeTypes: Set<PKGEdgeType>,
): FilteredSubgraph {
  const visibleNodes = nodes.filter(n => visibleTypes.has(n.entityType));
  const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
  const visibleEdges = edges.filter(
    e => visibleEdgeTypes.has(e.edgeType) && visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target),
  );
  return { nodes: visibleNodes, edges: visibleEdges };
}

type MatchTier = 0 | 1 | 2; // 0 = exact, 1 = prefix, 2 = substring

/**
 * Hand-rolled ranker (L9 — no lodash; it's a transitive-only dependency,
 * not a declared one). Exact > prefix > substring, case-insensitive,
 * stable insertion-order tie-break within a tier (V2), capped at 10.
 * Searches nodeLabel() (summary ?? entityId), matching what's actually
 * displayed on each node.
 */
export function rankLabelMatches(nodes: PKGNode[], query: string): PKGNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const tiered: Array<{ node: PKGNode; tier: MatchTier; index: number }> = [];
  nodes.forEach((node, index) => {
    const label = nodeLabel(node).toLowerCase();
    let tier: MatchTier | null = null;
    if (label === q) tier = 0;
    else if (label.startsWith(q)) tier = 1;
    else if (label.includes(q)) tier = 2;
    if (tier !== null) tiered.push({ node, tier, index });
  });

  tiered.sort((a, b) => (a.tier !== b.tier ? a.tier - b.tier : a.index - b.index));
  return tiered.slice(0, 10).map(t => t.node);
}
